import { Controller } from "@hotwired/stimulus"
import nacl from "tweetnacl"
import { encodeBase64, decodeBase64, encodeUTF8, decodeUTF8 } from "tweetnacl-util"

/**
 * Encryption Controller
 *
 * Handles client-side encryption of report data using X25519 + XSalsa20-Poly1305
 * via TweetNaCl's box primitive.
 *
 * The encryption format is:
 * - ephemeralPublicKey (32 bytes) + nonce (24 bytes) + ciphertext
 * - All base64 encoded for storage
 */
export default class extends Controller {
  static targets = [
    "toggle",
    "titleField",
    "descriptionField",
    "encryptedField",
    "nonceField",
    "publicKeySelect"
  ]

  static values = {
    publicKeys: Array // Array of { id, name, publicKey (base64) }
  }

  connect() {
    // Hide encryption option if no public keys available
    if (!this.publicKeysValue || this.publicKeysValue.length === 0) {
      this.element.querySelector('[data-encryption-section]')?.classList.add('hidden')
    }
  }

  toggle(event) {
    const enabled = event.target.checked
    const keySelect = this.element.querySelector('[data-encryption-key-select]')

    if (keySelect) {
      keySelect.classList.toggle('hidden', !enabled)
    }
  }

  async encrypt(event) {
    // If encryption is not enabled, let form submit normally
    if (!this.hasToggleTarget || !this.toggleTarget.checked) {
      return
    }

    event.preventDefault()

    try {
      // Get selected public key
      const selectedKeyId = this.publicKeySelectTarget.value
      const keyData = this.publicKeysValue.find(k => k.id === selectedKeyId)

      if (!keyData) {
        alert('Please select an encryption key')
        return
      }

      // Decode the recipient's public key
      const recipientPublicKey = decodeBase64(keyData.publicKey)

      // Get plaintext values
      const title = this.titleFieldTarget.value
      const description = this.descriptionFieldTarget.value

      // Create payload object
      const payload = JSON.stringify({ title, description })
      const payloadBytes = decodeUTF8(payload)

      // Generate ephemeral key pair for this encryption
      const ephemeralKeyPair = nacl.box.keyPair()

      // Generate random nonce
      const nonce = nacl.randomBytes(nacl.box.nonceLength)

      // Encrypt using box (X25519 + XSalsa20-Poly1305)
      const ciphertext = nacl.box(
        payloadBytes,
        nonce,
        recipientPublicKey,
        ephemeralKeyPair.secretKey
      )

      // Combine ephemeral public key + ciphertext
      // Format: ephemeralPubKey (32) || ciphertext (variable)
      const combined = new Uint8Array(ephemeralKeyPair.publicKey.length + ciphertext.length)
      combined.set(ephemeralKeyPair.publicKey)
      combined.set(ciphertext, ephemeralKeyPair.publicKey.length)

      // Encode to base64 for storage
      const encryptedPayload = encodeBase64(combined)
      const nonceB64 = encodeBase64(nonce)

      // Set encrypted values
      this.encryptedFieldTarget.value = encryptedPayload
      this.nonceFieldTarget.value = nonceB64

      // Clear/replace original fields with placeholder
      this.titleFieldTarget.value = "[Encrypted]"
      this.descriptionFieldTarget.value = "[Encrypted]"

      // Submit the form
      this.element.submit()

    } catch (error) {
      console.error('Encryption failed:', error)
      alert('Encryption failed: ' + error.message)
    }
  }
}
